// src/pages/team/TeamPortal.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "../../requests/axios";
import routes from "../../requests/routes";
import classes from "./Team.module.scss";

// --- helpers ----------------------------------------------------
const getDeptName = (m) =>
  m?.department?.name ||
  m?.department?.title ||
  m?.departmentName ||
  (typeof m?.department === "string" ? m.department : "") ||
  "Unassigned";

const getImg = (m) => m?.image || m?.photo || m?.avatar || "";
const getKey = (m, i) => m?._id || m?.id || `${getDeptName(m)}-${m?.name}-${i}`;
const textHaystack = (m) =>
  `${m?.name || ""} ${m?.role || ""} ${m?.location || ""}`.toLowerCase();

export const TeamPortal = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [q, setQ] = useState(""); // search (optional)
  const [cat, setCat] = useState("All"); // selected department tab
  const [cats, setCats] = useState(["All"]); // computed from data

  // fetch team (public) --------------------------------------------------------
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        // Use whichever endpoint you have; these fallbacks make it resilient
        const { data } = await axios.get(
          routes.teamPublicList || routes.teamList || "/api/team"
        );
        const list = data?.members || data?.team || data?.rows || data || [];
        setMembers(Array.isArray(list) ? list : []);

        // derive departments from members
        const uniqueDeps = Array.from(
          new Set(list.map(getDeptName).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setCats(["All", ...uniqueDeps]);
        // If current cat does not exist (e.g., after data change), reset it
        setCat((prev) => (prev && uniqueDeps.includes(prev) ? prev : "All"));
      } catch (e) {
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  // filtering -----------------------------------------------------
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return members.filter((m) => {
      const matchesCat = cat === "All" || getDeptName(m) === cat;
      const matchesQ = !qq || textHaystack(m).includes(qq);
      return matchesCat && matchesQ;
    });
  }, [members, q, cat]);

  return (
    <main className={classes.portal} id="main-content">
      {/* Banner */}
      <section className={classes.banner}>
        <div className={classes.banner__overlay}>
          <div className={classes.banner__pattern} aria-hidden="true" />
          <div className={classes.banner__body}>
            <h2 className={classes.banner__body__title}>
              Explore our Dedicated Team of Travel <span>Experts</span>
            </h2>
            <p className={classes.banner__body__text}>
              Meet the people crafting traveler-first programs and keeping every
              journey safe, smooth, and memorable.
            </p>
          </div>
        </div>
      </section>

      {/* Title */}
      <header className={classes.header} aria-labelledby="team-title">
        <h2 id="team-title" className={classes.header__title}>
          Our Team
        </h2>
        <p className={classes.header__subtitle}>
          Meet the people crafting traveler-first programs and keeping every
          journey safe, smooth, and memorable.
        </p>
      </header>

      {/* Controls */}
      <section className={classes.controls} aria-label="Team filters">
        {/* If you want search, just uncomment:
        <div className={classes.searchWrap}>
          <input
            type="text"
            className={classes.search}
            placeholder="Search by name, role, or location"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search team"
          />
        </div>
        */}
        <div className={classes.chips} role="tablist" aria-label="Departments">
          {cats.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cat === c}
              className={`${classes.chip} ${cat === c ? classes.active : ""}`}
              onClick={() => setCat(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Members (flex cards) */}
      <section className={classes.cards} aria-label="Team members">
        {loading && <div className={classes.empty}>Loading team…</div>}

        {!loading &&
          filtered.map((m, i) => {
            const dept = getDeptName(m);
            const photo = getImg(m);
            return (
              <article
                key={getKey(m, i)}
                className={classes.card}
                itemScope
                itemType="https://schema.org/Person"
              >
                <figure className={classes.avatar}>
                  {photo ? (
                    <img
                      className={classes.photo}
                      src={photo}
                      alt={`${m?.name || "Team member"} headshot`}
                      loading="lazy"
                      itemProp="image"
                    />
                  ) : (
                    <div className={classes.photoFallback} aria-hidden="true">
                      {(m?.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </figure>

                <div className={classes.info}>
                  <h3 className={classes.name} itemProp="name">
                    {m?.name || "Unnamed"}
                  </h3>
                  <div className={classes.meta}>
                    <span className={classes.role} itemProp="jobTitle">
                      {m?.role || "—"}
                    </span>
                    {(m?.location || "").trim() && (
                      <>
                        <span className={classes.dot} aria-hidden>
                          •
                        </span>
                        <span className={classes.location}>{m.location}</span>
                      </>
                    )}
                  </div>
                  {m?.bio && (
                    <p className={classes.bio} itemProp="description">
                      {m.bio}
                    </p>
                  )}
                </div>

                <span className={classes.cat}>{dept}</span>
              </article>
            );
          })}

        {!loading && filtered.length === 0 && (
          <div className={classes.empty}>
            No team members match your filters.
          </div>
        )}
      </section>
    </main>
  );
};

export default TeamPortal;
